import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { Check, X, Search, Filter, Sliders, User, Sparkles } from "lucide-react";
import styles from "./Step5MemberChooseMembership.module.css";
import { setUser } from "../../../../redux/modules/userSlice";
import { editProfileDataAPI } from "../../../../api/userApi";
import { queryKeys } from "../../../../query/keys";
import {
  PremiumLivestreamIcon,
  PrivateShowsIcon,
  PaidMediaIcon,
  TipsRequestsIcon,
} from "./Step5ConnectWaysIcons";
import { proceedToCCBillSubscription } from "../../../../services/membershipCheckout";
import ccBillIcon from "../../../../assets/images/ccBilll.png";

interface Step5Props {
  onNext: () => void;
  onBack: () => void;
}

export interface PlanData {
  id: string;
  name: string;
  tagline: string;
  taglineColor?: "pink" | "grey";
  description: string;
  price: string;
  period: string;
  buttonText: string;
  iconPath: string;
  isPopular?: boolean;
  popularBadgeText?: string;
  inheritedHeading?: string;
  includedFeatures: string[];
  creditsCount: number;
  creditsText: string;
  creditsValueText: string;
}

export const PLANS: PlanData[] = [
  {
    id: "onyx",
    name: "Onyx",
    tagline: "Take Control",
    taglineColor: "grey",
    description: "For members ready to go beyond basic discovery.",
    price: "$19.99",
    period: "/ month",
    buttonText: "Choose Onyx",
    iconPath: "/images/planicons/onyx.png",
    includedFeatures: [
      "Onyx Recognition Badge",
      "Advanced search",
      "Premium filters",
      "Unlimited messaging",
    ],
    creditsCount: 100,
    creditsText: "100 monthly credits",
    creditsValueText: "$10 standard credit value",
  },
  {
    id: "sapphire",
    name: "Sapphire",
    tagline: "Discover Smarter",
    taglineColor: "grey",
    description: "For members who want a faster, more intentional discovery experience.",
    price: "$49.99",
    period: "/ month",
    buttonText: "Choose Sapphire",
    iconPath: "/images/planicons/sapphire.png",
    inheritedHeading: "Inherited Onyx Benefits",
    includedFeatures: [
      "Sapphire Recognition Badge",
      "Enhanced recommendations",
      "Saved search",
    ],
    creditsCount: 300,
    creditsText: "300 monthly credits",
    creditsValueText: "$30 standard credit value",
  },
  {
    id: "diamond",
    name: "Diamond",
    tagline: "Unlock More",
    taglineColor: "pink",
    description: "For members who want smarter discovery and access to premium creator experiences.",
    price: "$99.99",
    period: "/ month",
    buttonText: "Choose Diamond",
    iconPath: "/images/planicons/diamond.png",
    isPopular: true,
    popularBadgeText: "RECOMMENDED",
    inheritedHeading: "Inherited Sapphire Benefits",
    includedFeatures: [
      "Diamond Recognition Badge",
      "Premium livestream access",
      "Personalized discovery",
      "Search alerts",
      "Premium creator experiences",
    ],
    creditsCount: 750,
    creditsText: "750 monthly credits",
    creditsValueText: "$75 standard credit value",
  },
  {
    id: "platinum",
    name: "Platinum VIP",
    tagline: "Experience The Highest Level",
    taglineColor: "grey",
    description: "For members seeking SugarCribs' highest level of recognition, access, and exclusivity.",
    price: "$249.99",
    period: "/ month",
    buttonText: "Choose Platinum VIP",
    iconPath: "/images/planicons/platinum.png",
    inheritedHeading: "Inherited Diamond Benefits",
    includedFeatures: [
      "Platinum VIP Recognition Badge",
      "Exclusive networking opportunities",
      "VIP events & experiences",
      "Priority Access to New Premium Features & Experiences",
    ],
    creditsCount: 2000,
    creditsText: "2,000 monthly credits",
    creditsValueText: "$200 standard credit value",
  },
];

export const CREDITS_MAP: Record<string, number> = {
  onyx: 100,
  sapphire: 300,
  diamond: 750,
  platinum: 2000,
};

export interface ConnectWayData {
  id: string;
  icon: "livestream" | "private" | "media" | "tips";
  title: string;
  description: string;
}

export const CONNECT_WAYS: ConnectWayData[] = [
  {
    id: "livestreams",
    icon: "livestream",
    title: "Premium Livestreams",
    description: "Access eligible Premium livestreams offered by participating creators.",
  },
  {
    id: "private-shows",
    icon: "private",
    title: "Private Shows",
    description: "Creators can offer private live experiences with their own",
  },
  {
    id: "paid-media",
    icon: "media",
    title: "Paid Photos & Videos",
    description: "Unlock individual premium content using credits.",
  },
  {
    id: "tips-requests",
    icon: "tips",
    title: "Tips & Custom Requests",
    description: "Support creators through tips and eligible custom experiences.",
  },
];

export interface FaqItem {
  question: string;
  answer: string;
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: "Is premium membership the same as subscribing to a creator ?",
    answer:
      "No. Premium membership Upgrades your platform wide experience. Creator subscriptions are separate and creator- Specific.",
  },
  {
    question: "Can I upgrade later?",
    answer:
      "Yes. Members using basic access can upgrade from Eligible membership surfaces after onboarding.",
  },
  {
    question: "What are credits used for?",
    answer:
      "Credits support eligible creator controlled premium experiences across SugarCribs.",
  },
];

const PinkCheckIcon = () => (
  <svg
    className={styles.checkIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.875 10C1.875 5.5125 5.5125 1.875 10 1.875C14.4875 1.875 18.125 5.5125 18.125 10C18.125 14.4875 14.4875 18.125 10 18.125C5.5125 18.125 1.875 14.4875 1.875 10ZM13.1442 7.24333C13.0776 7.19517 13.0022 7.1606 12.9223 7.14162C12.8423 7.12263 12.7594 7.11958 12.6783 7.13266C12.5972 7.14574 12.5194 7.17469 12.4495 7.21785C12.3796 7.261 12.3189 7.31752 12.2708 7.38417L9.27333 11.535C9.25497 11.5604 9.23111 11.5814 9.20353 11.5963C9.17595 11.6112 9.14536 11.6197 9.11404 11.6211C9.08271 11.6226 9.05147 11.6169 9.02264 11.6046C8.9938 11.5923 8.96812 11.5736 8.9475 11.55L7.6925 10.1167C7.63914 10.0527 7.57354 10.0001 7.49955 9.96185C7.42556 9.92361 7.34467 9.90055 7.26164 9.89402C7.17861 9.88749 7.09511 9.89762 7.01605 9.92381C6.93699 9.95001 6.86397 9.99174 6.80126 10.0466C6.73856 10.1014 6.68745 10.1682 6.65092 10.243C6.6144 10.3179 6.59321 10.3993 6.58859 10.4824C6.58396 10.5656 6.59601 10.6488 6.62402 10.7273C6.65202 10.8057 6.69542 10.8777 6.75167 10.9392L8.00667 12.3733C8.15112 12.5384 8.33096 12.6688 8.53279 12.7548C8.73462 12.8408 8.95325 12.8801 9.17239 12.8698C9.39153 12.8596 9.60553 12.8 9.79846 12.6956C9.99138 12.5912 10.1583 12.4445 10.2867 12.2667L13.2842 8.11667C13.3324 8.05015 13.367 7.97478 13.386 7.89488C13.4051 7.81497 13.4082 7.73209 13.3952 7.65098C13.3822 7.56986 13.3533 7.49211 13.3103 7.42216C13.2672 7.35222 13.2107 7.29145 13.1442 7.24333Z"
      fill="#FD3C84"
    />
  </svg>
);

const CreditCoinIcon = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.coinSvg}
  >
    <g filter="url(#filter0_f_14873_158126)">
      <path
        d="M13 15.5V26.5C13 27.88 15.239 29 18 29C19.126 29 20.164 28.814 21 28.5M21 28.5V20.5M21 28.5C21 29.88 23.239 31 26 31C28.761 31 31 29.88 31 28.5V20.5M23 18.5V15.5"
        stroke="url(#paint0_linear_14873_158126)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18 18C15.239 18 13 16.88 13 15.5C13 14.12 15.239 13 18 13C20.761 13 23 14.12 23 15.5C23 16.88 20.761 18 18 18Z"
        stroke="url(#paint1_linear_14873_158126)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 24.5C21 25.88 23.239 27 26 27C28.761 27 31 25.88 31 24.5M13 19.5C13 20.88 15.239 22 18 22C19.126 22 20.165 21.814 21 21.5M13 23C13 24.38 15.239 25.5 18 25.5C19.126 25.5 20.164 25.314 21 25M26 23C23.239 23 21 21.88 21 20.5C21 19.12 23.239 18 26 18C28.761 18 31 19.12 31 20.5C31 21.88 28.761 23 26 23Z"
        stroke="url(#paint2_linear_14873_158126)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <path
      d="M13 15.5V26.5C13 27.88 15.239 29 18 29C19.126 29 20.164 28.814 21 28.5M21 28.5V20.5M21 28.5C21 29.88 23.239 31 26 31C28.761 31 31 29.88 31 28.5V20.5M23 18.5V15.5"
      stroke="url(#paint3_linear_14873_158126)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18 18C15.239 18 13 16.88 13 15.5C13 14.12 15.239 13 18 13C20.761 13 23 14.12 23 15.5C23 16.88 20.761 18 18 18Z"
      stroke="url(#paint4_linear_14873_158126)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 24.5C21 25.88 23.239 27 26 27C28.761 27 31 25.88 31 24.5M13 19.5C13 20.88 15.239 22 18 22C19.126 22 20.165 21.814 21 21.5M13 23C13 24.38 15.239 25.5 18 25.5C19.126 25.5 20.164 25.314 21 25M26 23C23.239 23 21 21.88 21 20.5C21 19.12 23.239 18 26 18C28.761 18 31 19.12 31 20.5C31 21.88 28.761 23 26 23Z"
      stroke="url(#paint5_linear_14873_158126)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <filter
        id="filter0_f_14873_158126"
        x="-10"
        y="-10"
        width="64"
        height="64"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
        <feGaussianBlur stdDeviation="10" result="effect1_foregroundBlur_14873_158126" />
      </filter>
      <linearGradient
        id="paint0_linear_14873_158126"
        x1="13"
        y1="23.25"
        x2="31"
        y2="23.25"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECD3D" />
        <stop offset="0.39873" stopColor="#FFD761" />
        <stop offset="1" stopColor="#F1A21F" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_14873_158126"
        x1="13"
        y1="15.5"
        x2="23"
        y2="15.5"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECD3D" />
        <stop offset="0.39873" stopColor="#FFD761" />
        <stop offset="1" stopColor="#F1A21F" />
      </linearGradient>
      <linearGradient
        id="paint2_linear_14873_158126"
        x1="13"
        y1="22.5"
        x2="31"
        y2="22.5"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECD3D" />
        <stop offset="0.39873" stopColor="#FFD761" />
        <stop offset="1" stopColor="#F1A21F" />
      </linearGradient>
      <linearGradient
        id="paint3_linear_14873_158126"
        x1="13"
        y1="23.25"
        x2="31"
        y2="23.25"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECD3D" />
        <stop offset="0.39873" stopColor="#FFD761" />
        <stop offset="1" stopColor="#F1A21F" />
      </linearGradient>
      <linearGradient
        id="paint4_linear_14873_158126"
        x1="13"
        y1="15.5"
        x2="23"
        y2="15.5"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECD3D" />
        <stop offset="0.39873" stopColor="#FFD761" />
        <stop offset="1" stopColor="#F1A21F" />
      </linearGradient>
      <linearGradient
        id="paint5_linear_14873_158126"
        x1="13"
        y1="22.5"
        x2="31"
        y2="22.5"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECD3D" />
        <stop offset="0.39873" stopColor="#FFD761" />
        <stop offset="1" stopColor="#F1A21F" />
      </linearGradient>
    </defs>
  </svg>
);

export default function Step5MemberChooseMembership({ onNext }: Step5Props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<PlanData | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleProceedToCCBill = async (plan: PlanData) => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    try {
      await proceedToCCBillSubscription(plan, {
        dispatch,
        preMarkOnboarding: true,
      });
    } catch (err) {
      console.error("CCBill subscription redirect failed:", err);
      setIsRedirecting(false);
    }
  };

  const handleContinueBasicAccess = async () => {
    if (isCompleting) return;
    setIsCompleting(true);
    console.log("[Step5MemberChooseMembership] handleContinueBasicAccess called -> marking onboarding complete");

    try {
      await editProfileDataAPI({
        isOnboarding: true,
        validated: true,
        profileCompletion: true,
        phoneVerified: true,
      });
      console.log("[Step5MemberChooseMembership] Server updated with onboarding=true, validated=true, profileCompletion=true, phoneVerified=true");
    } catch (err) {
      console.warn("Could not mark onboarding complete on server:", err);
    }

    try {
      const stored = localStorage.getItem("user");
      const currentUser = stored ? JSON.parse(stored) : {};
      const updatedUser = {
        ...currentUser,
        isOnboarding: true,
        validated: true,
        emailVerified: true,
        phoneVerified: true,
        profileCompletion: true,
        userType: {
          _id: "6800a68085ec077c7c266c27",
          title: "viewer",
          description: "Member / Viewer",
        },
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.setItem("hasRegisteredOrLoggedIn", "true");
      dispatch(setUser(updatedUser));

      console.log("[Step5MemberChooseMembership] Updated user in localStorage/Redux:", updatedUser);

      // Update React Query cache so useGlobalUserInfo immediately receives updated userData
      queryClient.setQueryData(queryKeys.personalInfo, updatedUser);
      queryClient.invalidateQueries({ queryKey: queryKeys.personalInfo });
    } catch (e) {
      console.error("Error updating user state:", e);
    }

    // Scroll to top immediately so HomePage opens from the top
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Navigate to "/" where RootLayout and HomePage mount
    console.log("[Step5MemberChooseMembership] Navigating to '/' (Timeline/HomePage)");
    history.push("/");
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const scrollLeft = containerRef.current.scrollLeft;
      const cardWidth = containerRef.current.children[0]?.getBoundingClientRect().width || 306;
      const gap = 30;
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.max(0, Math.min(index, PLANS.length - 1)));
    }
  };

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    if (containerRef.current) {
      const cardWidth = containerRef.current.children[0]?.getBoundingClientRect().width || 306;
      const gap = 30;
      containerRef.current.scrollTo({
        left: index * (cardWidth + gap),
        behavior: "smooth",
      });
    }
  };

  return (
    <div className={styles.container}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroInnerContainer}>
          <div className={styles.heroContent}>
            <span className={styles.heroTag}>PREMIUM MEMBERSHIP</span>
            <div className={styles.heroTitleContainer}>
              <h2 className={styles.heroTitle}>
                Elevate your SugarCribs<br />experience.
              </h2>
            </div>
            <h3 className={styles.heroSubtitle}>Discover better. Connect deeper</h3>
            <p className={styles.heroDescription}>
              Unlock more powerful discovery, unlimited messaging, premium recognition, exclusive access, and monthly credits.
            </p>
          </div>

          <div className={styles.heroImageWrapper}>
            <img
              src="/images/membership-hero-bg.jpg"
              alt="SugarCribs Premium Membership"
              className={styles.heroImage}
            />
          </div>
        </div>
      </div>

      {/* 1. Main 4 Subscription Cards Section */}
      <h1 className={styles.formTitle}>Choose a Plan</h1>

      <div className={styles.plansGrid} ref={containerRef} onScroll={handleScroll}>
        {PLANS.map((plan) => {
          const {
            id,
            name,
            tagline,
            taglineColor,
            description,
            price,
            period,
            buttonText,
            iconPath,
            isPopular,
            popularBadgeText,
            inheritedHeading,
            includedFeatures,
            creditsText,
            creditsValueText,
          } = plan;

          return (
            <div
              key={id}
              className={`${styles.planCard} ${isPopular ? styles.planCardRecommended : ""} ${id === "platinum" ? styles.planCardPlatinum : ""
                }`}
              onClick={() => setSelectedPlan(plan)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedPlan(plan);
                }
              }}
            >
              {isPopular && popularBadgeText && (
                <div className={styles.recommendedBadge}>{popularBadgeText}</div>
              )}

              {/* Card Header: Icon + Title & Tagline */}
              <div className={styles.cardHeader}>
                <div className={styles.iconContainer}>
                  <img src={iconPath} alt={`${name} Icon`} className={styles.planIconImage} />
                </div>
                <div className={styles.titleInfo}>
                  <h3 className={styles.planName}>{name}</h3>
                  <span
                    className={
                      taglineColor === "pink" ? styles.planTaglinePink : styles.planTaglineGrey
                    }
                  >
                    {tagline}
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className={styles.planDescription}>{description}</p>

              {/* Price & Billing Cycle */}
              <div className={styles.priceContainer}>
                <span className={styles.price}>{price}</span>
                <span className={styles.period}>{period}</span>
              </div>

              {/* CTA Button */}
              <button
                type="button"
                className={isPopular ? styles.chooseBtnRecommended : styles.chooseBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPlan(plan);
                }}
              >
                {buttonText}
              </button>

              {/* What's Included Section Divider */}
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>What's Included</span>
                <div className={styles.dashedDivider} />
              </div>

              {/* Inherited Benefits Heading (if any) */}
              <div className={styles.includedSection}>
                {inheritedHeading && (
                  <div className={styles.inheritedHeadingText}>{inheritedHeading}</div>
                )}
                <div className={styles.featuresList}>
                  {includedFeatures.map((feat, idx) => (
                    <div key={idx} className={styles.featureItem}>
                      <PinkCheckIcon />
                      <span className={styles.featureText}>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Inset: Monthly Credits */}
              <div className={styles.creditsBox}>
                <div className={styles.coinIconCircle}>
                  <CreditCoinIcon />
                </div>
                <div className={styles.creditsText}>
                  <span className={styles.creditsTitle}>{creditsText}</span>
                  <span className={styles.creditsSubtitle}>{creditsValueText}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile/Tablet Dots Indicator */}
      <div className={styles.dotsContainer}>
        {PLANS.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`${styles.dot} ${index === activeIndex ? styles.activeDot : ""}`}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* 2. More Ways to Connect Section */}
      <div className={styles.waysSection}>
        <h2 className={styles.waysTitle}>More Ways to Connect</h2>
        <p className={styles.waysSubtitle}>
          Creators can choose how they monetize their content and live experiences. Depending on your settings and
          eligibility, members may use credits for premium interactions across SugarCribs.
        </p>

        <div className={styles.waysGrid}>
          {CONNECT_WAYS.map((way) => (
            <div key={way.id} className={styles.wayCard}>
              <div className={styles.wayIconWrapper}>
                {way.icon === "livestream" && <PremiumLivestreamIcon />}
                {way.icon === "private" && <PrivateShowsIcon />}
                {way.icon === "media" && <PaidMediaIcon />}
                {way.icon === "tips" && <TipsRequestsIcon />}
              </div>
              <h3 className={styles.wayTitle}>{way.title}</h3>
              <p className={styles.wayDescription}>{way.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 3. Not ready for Premium? Banner */}
      <div className={styles.notReadyBanner}>
        <div className={styles.notReadyContent}>
          <h2 className={styles.notReadyTitle}>Not ready for Premium?</h2>
          <p className={styles.notReadySubtitle}>Get the standard SugarCribs experience.</p>
        </div>
        <div className={styles.notReadyCtaWrapper}>
          <button
            type="button"
            className={styles.continueBasicBtn}
            onClick={handleContinueBasicAccess}
            disabled={isCompleting}
          >
            {isCompleting ? "Loading..." : "Continue With Basic Access"}
          </button>
          <span className={styles.notReadyUpgradeNote}>You can upgrade anytime</span>
        </div>
      </div>

      {/* 4. Quick answers (FAQ) Section */}
      <div className={styles.quickAnswersSection}>
        <div className={styles.quickAnswersHeader}>
          <h2 className={styles.quickAnswersTitle}>Quick answers</h2>
          <p className={styles.quickAnswersSubtitle}>
            Common questions about memberships, credits, and creator monetization.
          </p>
        </div>
        <div className={styles.quickAnswersGrid}>
          {FAQ_ITEMS.map((faq, index) => (
            <div key={index} className={styles.faqCard}>
              <h3 className={styles.faqQuestion}>{faq.question}</h3>
              <p className={styles.faqAnswer}>{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CCBill Payment Modal */}
      {selectedPlan && (
        <div className={styles.modalOverlay} onClick={() => setSelectedPlan(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => setSelectedPlan(null)}
              aria-label="Close"
            >
              <X size={20} strokeWidth={1.75} color="#ffffff" />
            </button>

            <div className={styles.modalHeader}>
              <div className={styles.modalIconContainer}>
                <img
                  src={selectedPlan.iconPath}
                  alt={`${selectedPlan.name} Icon`}
                  className={styles.modalIconImageLarge}
                />
              </div>
              <h2 className={styles.modalTitle}>Secure Subscription Payment</h2>
              <p className={styles.modalSubtitle}>
                Subscribe to {selectedPlan.name} for {selectedPlan.price} per month
              </p>
            </div>

            <div className={styles.modalDetails}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Your purchase:</span>
                <span className={styles.detailValue}>{selectedPlan.name}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Included Credits:</span>
                <span className={styles.detailValue}>
                  {selectedPlan.creditsCount || CREDITS_MAP[selectedPlan.id] || 0}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Total:</span>
                <span className={styles.detailValue}>{selectedPlan.price}</span>
              </div>
            </div>

            <button
              type="button"
              className={styles.payBtn}
              disabled={isRedirecting || isCompleting}
              onClick={() => handleProceedToCCBill(selectedPlan)}
            >
              <img src={ccBillIcon} alt="CCBill" className={styles.ccBillImg} />
              <span className={styles.payBtnText}>
                {isRedirecting ? "Redirecting..." : "Pay With CCBill"}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
